import React from 'react';
import { getAppInsights } from '../../infrastructure/Logging/TelemetryService';
import { toast } from 'react-toastify';
import { saveErrorPayload } from './errorPayloadStorage';

const buildErrorPageUrl = (originPath?: string) => {
  if (!originPath) return '/error';
  const encoded = encodeURIComponent(originPath);
  return `/error?from=${encoded}`;
};

type Props = {
  children: React.ReactNode;
  fallback?: React.ReactNode;
};

type State = {
  hasError: boolean;
  error?: Error;
};

export class AppErrorBoundary extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    try {
      const app = getAppInsights();
      app?.trackException({ exception: error });
      app?.trackTrace({ message: 'error-boundary', properties: { errorInfo: JSON.stringify(errorInfo) } });
    } catch {}

    // Reload or redirect without showing a fallback page
    try {
      const now = Date.now();
      const last = Number(sessionStorage.getItem('app_error_last') || '0');
      const count = Number(sessionStorage.getItem('app_error_count') || '0');
      const withinWindow = last && (now - last) < 60_000; // 60s window
      const nextCount = withinWindow ? count + 1 : 1;
      sessionStorage.setItem('app_error_last', String(now));
      sessionStorage.setItem('app_error_count', String(nextCount));

      if (nextCount <= 2) {
        // Attempt to recover with a reload on the same route
        window.location.reload();
      } else {
        // Too many consecutive errors: send user to home
        toast.error('¡Uy! Ocurrió un error inesperado. Te llevamos a la página de error.');
        const originPath = `${window.location.pathname}${window.location.search}${window.location.hash}`;
        saveErrorPayload({
          message: error.message,
          stack: error.stack,
          componentStack: errorInfo.componentStack ?? undefined,
          originPath,
          source: 'error-boundary',
        });
        window.location.assign(buildErrorPageUrl(originPath));
      }
    } catch {
      // Fallback to a simple reload
      window.location.reload();
    }
  }

  handleReload = () => {
    // Force a hard reload to clear any bad runtime state
    window.location.reload();
  };

  render() {
    // Do not show an error UI; auto reload/redirect handled in componentDidCatch
    if (this.state.hasError) return null;
    return this.props.children;
  }
}

export default AppErrorBoundary;
